import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OmniGateway } from '@cigna/omni/shared-util';
import {
  ConversationDTO,
  ConversationMessageEventDTO,
  ConversationTransferResultDTO,
  ConversationActivityEventDTO,
  ConversationClient,
  ConversationSummaryDTO,
} from '@cigna/vampire-dto';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { Transfer } from '../models';

interface AssistantOutcome {
  status?: number;
  code?: number;
  type?: string;
  message?: string;
  additionalDetails?: Array<{ message?: string; severity?: string }>;
}

interface AssistantMetadata {
  outcome?: AssistantOutcome;
  [key: string]: unknown;
}

/**
 * The converse assistant (/assistant/v2) responds with the Cigna response
 * model `{ data, metadata: { outcome } }`, while the legacy bot (/bot/v1)
 * responds with the payload itself and `metadata` alongside it.
 */
interface AssistantResponseEnvelope<T> {
  data?: T | null;
  metadata?: AssistantMetadata;
}

interface AssistantNormalizeOptions {
  /**
   * When the envelope has no `data`, emit `{ metadata }` (the legacy
   * outcome-only shape the effects already check) instead of erroring.
   */
  emitOutcomeOnly?: (outcome: AssistantOutcome | undefined) => boolean;
  /**
   * Also apply `emitOutcomeOnly` to HTTP error bodies, because the assistant
   * reports failure outcomes with a non-2xx status.
   */
  shouldIncludeHttpErrors?: boolean;
}

const isOutageOutcome = (outcome: AssistantOutcome | undefined) =>
  outcome?.status === 501 && !!outcome.additionalDetails?.[0]?.message;

const isFailedOutcome = (outcome: AssistantOutcome | undefined) =>
  (outcome?.status ?? 0) >= 400;

@Injectable({
  providedIn: 'root',
})
export class ConversationsDataAccessService {
  private isBotAssistantV2 = false;

  constructor(private http: HttpClient, private gateway: OmniGateway) {}

  public setBotAssistantV2(enabled: boolean | undefined): void {
    this.isBotAssistantV2 = enabled === true;
  }

  public endLiveConversation(
    conversationId: string,
  ): Observable<ConversationDTO> {
    const url = this.gateway.endLiveConversationUrl({ conversationId });
    return this.http.post<ConversationDTO>(url, {});
  }
  public endBotConversation(
    conversationId: string,
  ): Observable<ConversationDTO> {
    const url = this.gateway.endBotConversationUrl({
      conversationId,
      botAssistantV2: this.isBotAssistantV2,
    });
    if (!this.isBotAssistantV2) {
      return this.http.post<ConversationDTO>(url, {});
    }
    return this.normalizeAssistantResponse(
      this.http.patch<AssistantResponseEnvelope<ConversationDTO> | null>(
        url,
        {},
      ),
      url,
    );
  }

  public terminateConversation(
    conversationId: string,
    userAction: 'logout' | 'inactive',
  ): Observable<void> {
    const url = this.gateway.terminateConversationUrl({
      conversationId,
      userAction,
    });
    return this.http.post<void>(url, {});
  }

  public terminateLambdaConversation(
    conversationId: string,
    ssoId: string,
    userAction: 'logout' | 'inactive',
    type: 'user' | 'bot',
  ): Observable<void> {
    const url = this.gateway.terminateLambdaConversationUrl(userAction, type);
    return this.http.post<void>(url, { ssoId, convId: conversationId });
  }

  public getBotConversation(conversationId: string) {
    const url = this.gateway.getBotConversationUrl({
      conversationId,
      botAssistantV2: this.isBotAssistantV2,
    });
    if (!this.isBotAssistantV2) {
      return this.http.get<ConversationDTO>(url);
    }
    return this.normalizeAssistantResponse(
      this.http.get<AssistantResponseEnvelope<ConversationDTO> | null>(url),
      url,
    );
  }
  public getLiveConversation(
    conversationId: string,
    enableNewDMUI?: boolean,
    convType?: string,
  ) {
    const url =
      convType === 'async'
        ? this.gateway.getAwsLiveConversationUrl({ conversationId }) +
          this.appendAsyncQuery(enableNewDMUI)
        : this.gateway.getLiveConversationUrl({ conversationId });
    return this.http.get<ConversationDTO>(url);
  }

  // The static assistant has no list route, so loading conversations stays
  // on bot v1 until the Kore cleanup story removes it.
  public loadConversations() {
    const url = this.gateway.conversationsUrl();
    return this.http.get<ConversationDTO[]>(url);
  }

  public botMessageViewed(conversationId: string, messageId: string) {
    const url = this.gateway.botMessageViewedUrl({
      conversationId,
      messageId,
      botAssistantV2: this.isBotAssistantV2,
    });
    if (!this.isBotAssistantV2) {
      return this.http.post<ConversationDTO['events']>(url, {});
    }
    // A failed outcome is emitted as `{ metadata }`, which the viewed effect
    // already maps to CreateConversationFailure.
    return this.normalizeAssistantResponse(
      this.http.post<AssistantResponseEnvelope<
        ConversationDTO['events']
      > | null>(url, {}),
      url,
      { emitOutcomeOnly: isFailedOutcome },
    );
  }
  public liveMessageViewed(
    conversationId: string,
    messageId: string,
    enableNewDMUI?: boolean,
    convType?: string,
  ) {
    const url =
      convType === 'async'
        ? this.gateway.awsLiveMessageViewedUrl({
            conversationId,
            messageId,
          }) + this.appendAsyncQuery(enableNewDMUI)
        : this.gateway.liveMessageViewedUrl({
            conversationId,
            messageId,
          });
    return this.http.post<ConversationDTO['events']>(url, {});
  }

  public activeConversation() {
    const url = this.gateway.activeConversation();
    return this.http.get<Transfer>(url);
  }

  public activeAsyncConversation() {
    const url = this.gateway.activeAsyncConversation();
    return this.http.get<ConversationDTO[]>(url);
  }

  public conversationHistory() {
    const url = this.gateway.conversationHistory();
    return this.http.get<ConversationSummaryDTO[]>(url);
  }

  public directMessageConversationHistory() {
    const url = this.gateway.directMessageConversationHistory();
    return this.http.get<ConversationSummaryDTO[]>(url);
  }

  public postActivity(
    conversationId: string,
    activityType: ConversationActivityEventDTO['activityType'],
  ) {
    const body = {
      activityType,
    };
    const url = this.gateway.postActivity({ conversationId });
    return this.http.post<ConversationActivityEventDTO>(url, body);
  }

  public postConversation(
    type: ConversationDTO['type'],
    context: ConversationDTO['context'],
    initial?: string,
  ): Observable<ConversationDTO> {
    const body = {
      type,
      context,
      client: {
        type: 'browser' as ConversationClient['type'],
        fcmToken: '',
      },
    };
    const url = this.gateway.postBotConversationUrl(this.isBotAssistantV2);
    const payload = initial ? { ...body, initial } : body;
    if (!this.isBotAssistantV2) {
      return this.http.post<ConversationDTO>(url, payload);
    }
    // An outage outcome is emitted as `{ metadata }`, which the create effect
    // already uses to show the chat outage message.
    return this.normalizeAssistantResponse(
      this.http.post<AssistantResponseEnvelope<ConversationDTO> | null>(
        url,
        payload,
      ),
      url,
      { emitOutcomeOnly: isOutageOutcome, shouldIncludeHttpErrors: true },
    );
  }

  public postLiveConversation(
    type: ConversationDTO['type'],
    context: ConversationDTO['context'],
  ): Observable<ConversationDTO> {
    const body = {
      type,
      context,
      client: {
        type: 'browser' as ConversationClient['type'],
        fcmToken: '',
      },
    };
    const url = this.gateway.postLiveConversationUrl();
    return this.http.post<ConversationDTO>(url, body);
  }

  // The static assistant has no CTA route, so CTAs stay on bot v1 until the
  // Kore cleanup story removes them.
  public postCta(
    conversationId: string,
    sourceMessageId: string,
    value: string,
    _id: string,
  ): Observable<ConversationMessageEventDTO> {
    const url = this.gateway.postCtaUrl({ conversationId });
    return this.http.post<ConversationMessageEventDTO>(url, {
      value,
      sourceMessageId,
      params: {},
      _id,
    });
  }

  public postMessage(
    conversationId: string,
    message: string,
    enableNewDMUI?: boolean,
    convType?: string,
  ): Observable<ConversationMessageEventDTO> {
    const url =
      convType === 'async'
        ? this.gateway.awsPostMessageUrl({ conversationId }) +
          this.appendAsyncQuery(enableNewDMUI)
        : this.gateway.postMessageUrl({ conversationId });
    return this.http.post<ConversationMessageEventDTO>(url, { message });
  }

  public transferConversation(
    conversationId: string,
    type: ConversationDTO['type'],
    initial: string = '',
  ): Observable<ConversationTransferResultDTO> {
    const url = this.gateway.transferConversationUrl({ conversationId });
    return this.http.post<ConversationTransferResultDTO>(url, {
      type,
      initial,
      client: {
        type: 'browser' as ConversationClient['type'],
      },
    });
  }

  appendAsyncQuery(enableNewDMUI: boolean | undefined) {
    return enableNewDMUI ? '?chatType=async' : '';
  }

  /**
   * Converts an assistant `{ data, metadata }` envelope into the legacy bot v1
   * response shape the conversation effects expect: the payload with its
   * `metadata` alongside (arrays are returned as-is, as bot v1 did). A missing
   * `data` becomes `{ metadata }` when `emitOutcomeOnly` matches the outcome,
   * otherwise an HttpErrorResponse, so the effects take their error path
   * instead of reading properties of null.
   */
  private normalizeAssistantResponse<T>(
    response$: Observable<AssistantResponseEnvelope<T> | null>,
    url: string,
    options: AssistantNormalizeOptions = {},
  ): Observable<T> {
    const { emitOutcomeOnly, shouldIncludeHttpErrors = false } = options;

    return response$.pipe(
      catchError((error: unknown) => {
        const metadata = this.getErrorMetadata(error);
        return shouldIncludeHttpErrors && emitOutcomeOnly?.(metadata?.outcome)
          ? of<AssistantResponseEnvelope<T>>({ metadata })
          : throwError(() => error);
      }),
      mergeMap((response) =>
        this.toLegacyResponse(response, url, emitOutcomeOnly),
      ),
    );
  }

  private toLegacyResponse<T>(
    response: AssistantResponseEnvelope<T> | null,
    url: string,
    emitOutcomeOnly: AssistantNormalizeOptions['emitOutcomeOnly'],
  ): Observable<T> {
    if (response !== null && !this.isAssistantEnvelope(response)) {
      return of(response as T);
    }
    const { data, metadata } = response ?? {};
    const isOutcomeOnly = emitOutcomeOnly?.(metadata?.outcome) === true;
    const outcomeOnly = { metadata } as unknown as T;

    if (data === null || data === undefined) {
      return isOutcomeOnly
        ? of(outcomeOnly)
        : throwError(() => this.toMissingDataError(response, url));
    }
    if (Array.isArray(data)) {
      return of(isOutcomeOnly ? outcomeOnly : (data as T));
    }
    return of((metadata === undefined ? data : { ...data, metadata }) as T);
  }

  private toMissingDataError<T>(
    response: AssistantResponseEnvelope<T> | null,
    url: string,
  ): HttpErrorResponse {
    const outcome = response?.metadata?.outcome;
    return new HttpErrorResponse({
      error: response,
      status: isFailedOutcome(outcome) ? outcome?.status : 500,
      statusText: outcome?.message ?? 'Unknown Error',
      url,
    });
  }

  private isAssistantEnvelope(response: unknown): boolean {
    return (
      typeof response === 'object' &&
      response !== null &&
      !Array.isArray(response) &&
      ('data' in response ||
        Object.keys(response).every((key) => key === 'metadata'))
    );
  }

  private getErrorMetadata(error: unknown): AssistantMetadata | undefined {
    const body: unknown =
      error instanceof HttpErrorResponse ? error.error : undefined;
    return typeof body === 'object' && body !== null && 'metadata' in body
      ? (body as AssistantResponseEnvelope<unknown>).metadata
      : undefined;
  }
}
